// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import { Formik, Form, FieldGroup } from 'shared/components/form';
import Button from 'shared/components/common/Button';
import VerifyTwoFactorMutation from 'shared/mutations/VerifyTwoFactor';
import SetupTwoFactorMutation from 'shared/mutations/SetupTwoFactor';
import SendTwoFactorSetupCodeMutation from 'shared/mutations/SendTwoFactorSetupCode';
import ResendTwoFactorCodeMutation from 'shared/mutations/ResendTwoFactorCode';
import { colors, fonts } from 'shared/styleguide';

type Props = {
  mode: 'setup' | 'login',
  token: string,
  onSuccess: () => void,
  onBack: () => void,
  onError: (message: string) => void,
};

const Header = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.charcoalGrey};
  margin-bottom: 24px;
`;

const RememberWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0 24px 0;
  ${fonts.smallRegular};
  color: ${colors.charcoalGrey};
  cursor: pointer;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 16px;
  color: ${colors.azul};
  ${fonts.smallRegular};
  cursor: pointer;
  text-decoration: underline;
`;

const BackButton = styled(LinkButton)`
  margin-top: 24px;
  display: block;
`;

export default function TwoFactorAuthFlow({
  mode,
  token,
  onSuccess,
  onBack,
  onError,
}: Props) {
  const [codeSent, setCodeSent] = useState(mode === 'login');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);

  const handleSendSetupCode = async (phone: string) => {
    setSendingCode(true);
    try {
      await SendTwoFactorSetupCodeMutation.commit({
        variables: {
          input: {
            setupToken: token,
            phone,
          },
        },
      });
      setCodeSent(true);
    } catch (e) {
      onError(e.message || e);
    } finally {
      setSendingCode(false);
    }
  };

  const handleResendLoginCode = async () => {
    setResendingCode(true);
    try {
      await ResendTwoFactorCodeMutation.commit({
        variables: {
          input: {
            twoFactorToken: token,
          },
        },
      });
    } catch (e) {
      onError(e.message || e);
    } finally {
      setResendingCode(false);
    }
  };

  if (mode === 'setup') {
    return (
      <>
        <Header>
          {i18n.t(
            'Two-factor authentication is required for your account. Verify your phone number to continue.',
          )}
        </Header>
        <Formik
          initialValues={{ phone: '', code: '' }}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
              await SetupTwoFactorMutation.commit({
                variables: {
                  input: {
                    setupToken: token,
                    phone: values.phone.trim(),
                    code: values.code.trim(),
                  },
                },
              });
              onSuccess();
            } catch (e) {
              setSubmitting(false);
              onError(e.message || e);
            }
          }}
          render={({ values, isSubmitting, handleSubmit, setFieldValue }) => (
            <Form>
              <FieldGroup
                label={i18n.t('Phone Number')}
                name="phone"
                placeholder="+15551234567"
                description={i18n.t('Include country code, e.g. +1 for US')}
              />
              {!codeSent ? (
                <Button
                  type="button"
                  loading={sendingCode}
                  disabled={!values.phone.trim()}
                  onClick={() => handleSendSetupCode(values.phone.trim())}
                >
                  {i18n.t('Send Verification Code')}
                </Button>
              ) : (
                <>
                  <FieldGroup
                    label={i18n.t('Verification Code')}
                    name="code"
                    placeholder="123456"
                  />
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    disabled={!values.code.trim()}
                    onClick={handleSubmit}
                  >
                    {i18n.t('Verify & Sign In')}
                  </Button>
                  <LinkButton
                    type="button"
                    onClick={() => {
                      setCodeSent(false);
                      setFieldValue('code', '');
                    }}
                  >
                    {i18n.t('Use a different phone number')}
                  </LinkButton>
                </>
              )}
              <BackButton type="button" onClick={onBack}>
                {i18n.t('Back to sign in')}
              </BackButton>
            </Form>
          )}
        />
      </>
    );
  }

  return (
    <>
      <Header>
        {i18n.t('Enter the verification code sent to your phone.')}
      </Header>
      <Formik
        initialValues={{ code: '' }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          try {
            await VerifyTwoFactorMutation.commit({
              variables: {
                input: {
                  twoFactorToken: token,
                  code: values.code.trim(),
                  rememberDevice,
                },
              },
            });
            onSuccess();
          } catch (e) {
            setSubmitting(false);
            onError(e.message || e);
          }
        }}
        render={({ values, isSubmitting, handleSubmit }) => (
          <Form>
            <FieldGroup
              label={i18n.t('Verification Code')}
              name="code"
              placeholder="123456"
            />
            <RememberWrapper>
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={e => setRememberDevice(e.target.checked)}
              />
              {i18n.t('Remember this browser for 30 days')}
            </RememberWrapper>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!values.code.trim()}
              onClick={handleSubmit}
            >
              {i18n.t('Verify & Sign In')}
            </Button>
            <LinkButton
              type="button"
              disabled={resendingCode}
              onClick={handleResendLoginCode}
            >
              {resendingCode ? i18n.t('Sending...') : i18n.t('Resend code')}
            </LinkButton>
            <BackButton type="button" onClick={onBack}>
              {i18n.t('Back to sign in')}
            </BackButton>
          </Form>
        )}
      />
    </>
  );
}
